/**
 * First-touch attribution: where this browser first came to the site from. Recorded once, on the first page load,
 * and kept in localStorage (every read and write is wrapped, so private modes and blocked storage only lose it).
 *
 * Why it exists: utm_* tags reach few visits (43,019 of 45,124 visitors in the 90 days to 2026-10-23 had none), and
 * the session tags in hooks/useUtmPersistence.ts are latest-touch. A reader from Google, organic Reddit or a typed
 * address left nothing behind, so an email signup or an affiliate click could not be tied to the channel that
 * brought them. captureEmail() adds this record to the signup's tags and the GA4 affiliate_click event carries its
 * source and medium (lib/analytics.ts).
 *
 * Source and medium come from the landing URL's utm_* tags when it has any; else from an ad click id (rdt_cid is a
 * Reddit ad click, gclid a Google ad click); else, when the visit has no outside referrer, from the ad parameters
 * the static landers stored (lib/attribution.ts, the same localStorage key); else from the referrer; else
 * "(direct)" / "(none)", GA4's words. Mediums inferred here: organic (search engines), social, ai (chat assistants),
 * email (webmail and the newsletter's web pages), paid, cpc and referral.
 */
import { getAttribution } from "@/lib/attribution";

export const FIRST_TOUCH_KEY = "bhg_first_touch";

export interface FirstTouch {
  source: string;
  medium: string;
  campaign: string;
  /** The referring site's host without "www.", or "" for none and for this site's own pages. */
  referrer_domain: string;
  /** The path of the first page, without the query string. */
  landing_path: string;
  /** ISO timestamp of the first visit. */
  ts: string;
  /** Reddit's ad click id, when the landing URL (or a lander before it) carried one. */
  rdt_cid?: string;
}

type Channel = { name: string; medium: string; hosts: RegExp };

// Checked in this order, so mail.google.com is email and gemini.google.com is ai before google.* is search.
const CHANNELS: Channel[] = [
  { name: "", medium: "email", hosts: /^(mail\.google\.com|mail\.yahoo\.com|outlook\.(live|office|office365)\.com|mail\.proton\.me|mail\.aol\.com|([a-z0-9-]+\.)*beehiiv\.com)$/ },
  { name: "", medium: "ai", hosts: /^(chatgpt\.com|chat\.openai\.com|([a-z0-9-]+\.)*perplexity\.ai|copilot\.microsoft\.com|gemini\.google\.com|claude\.ai|you\.com|phind\.com|meta\.ai|grok\.com|chat\.deepseek\.com|poe\.com)$/ },
  { name: "google", medium: "organic", hosts: /^([a-z0-9-]+\.)*google\.[a-z.]+$/ },
  { name: "bing", medium: "organic", hosts: /^([a-z0-9-]+\.)*bing\.com$/ },
  { name: "duckduckgo", medium: "organic", hosts: /^([a-z0-9-]+\.)*duckduckgo\.com$/ },
  { name: "yahoo", medium: "organic", hosts: /^([a-z0-9-]+\.)*yahoo\.[a-z.]+$/ },
  { name: "ecosia", medium: "organic", hosts: /^([a-z0-9-]+\.)*ecosia\.org$/ },
  { name: "brave", medium: "organic", hosts: /^search\.brave\.com$/ },
  { name: "yandex", medium: "organic", hosts: /^([a-z0-9-]+\.)*yandex\.[a-z.]+$/ },
  { name: "baidu", medium: "organic", hosts: /^([a-z0-9-]+\.)*baidu\.com$/ },
  { name: "startpage", medium: "organic", hosts: /^([a-z0-9-]+\.)*startpage\.com$/ },
  { name: "qwant", medium: "organic", hosts: /^([a-z0-9-]+\.)*qwant\.com$/ },
  { name: "reddit", medium: "social", hosts: /^(([a-z0-9-]+\.)*reddit\.com|redd\.it)$/ },
  { name: "facebook", medium: "social", hosts: /^(([a-z0-9-]+\.)*facebook\.com|fb\.me)$/ },
  { name: "instagram", medium: "social", hosts: /^([a-z0-9-]+\.)*instagram\.com$/ },
  { name: "x", medium: "social", hosts: /^(([a-z0-9-]+\.)*(twitter|x)\.com|t\.co)$/ },
  { name: "youtube", medium: "social", hosts: /^(([a-z0-9-]+\.)*youtube\.com|youtu\.be)$/ },
  { name: "tiktok", medium: "social", hosts: /^([a-z0-9-]+\.)*tiktok\.com$/ },
  { name: "linkedin", medium: "social", hosts: /^(([a-z0-9-]+\.)*linkedin\.com|lnkd\.in)$/ },
  { name: "pinterest", medium: "social", hosts: /^([a-z0-9-]+\.)*pinterest\.[a-z.]+$/ },
  { name: "threads", medium: "social", hosts: /^([a-z0-9-]+\.)*threads\.(net|com)$/ },
  { name: "discord", medium: "social", hosts: /^(([a-z0-9-]+\.)*discord\.com|discord\.gg)$/ },
  { name: "telegram", medium: "social", hosts: /^(t\.me|([a-z0-9-]+\.)*telegram\.org)$/ },
  { name: "quora", medium: "social", hosts: /^([a-z0-9-]+\.)*quora\.com$/ },
];

const OWN_SITE = /^([a-z0-9-]+\.)*bodyhackguide\.co$/;

function channelFor(value: string): Channel | null {
  const v = value.toLowerCase();
  return CHANNELS.find((c) => (c.name && c.name === v) || c.hosts.test(v)) ?? null;
}

/** Trimmed, control characters out, capped. */
function clean(value: string | null | undefined, max: number): string {
  return (value ?? "").replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, max);
}

function hostOf(referrer: string): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

export interface FirstTouchInput {
  /** The landing URL's query string. */
  search: string;
  /** document.referrer. */
  referrer: string;
  pathname: string;
  /** This page's own host, so a same-site referrer (another tab, a static page of the site) counts as none. */
  hostname: string;
  /** Ad parameters stored earlier on this browser (lib/attribution.ts), used only for a visit with no outside referrer. */
  stored?: Record<string, string>;
  now?: Date;
}

/** The first-touch record for one landing. Pure, so the rules are testable. */
export function classifyFirstTouch(input: FirstTouchInput): FirstTouch {
  const ownHost = input.hostname.toLowerCase().replace(/^www\./, "");
  const rawHost = hostOf(input.referrer);
  const referrerDomain = rawHost && rawHost !== ownHost && !OWN_SITE.test(rawHost) ? rawHost : "";

  const fromUrl: Record<string, string> = {};
  try {
    new URLSearchParams(input.search).forEach((value, key) => {
      if (value) fromUrl[key.toLowerCase()] = value;
    });
  } catch {
    /* unparseable query: treat as none */
  }
  const tracked = (bag: Record<string, string>) =>
    ["utm_source", "utm_medium", "utm_campaign", "rdt_cid", "gclid", "gbraid", "wbraid", "msclkid", "fbclid"].some((k) => !!bag[k]);
  // A lander visit that moved into the SPA arrives with no tags and this site as the referrer; the lander stored them.
  let params = fromUrl;
  if (!tracked(fromUrl) && !referrerDomain && input.stored) {
    const stored: Record<string, string> = {};
    for (const [key, value] of Object.entries(input.stored)) if (value) stored[key.toLowerCase()] = value;
    if (tracked(stored)) params = stored;
  }

  let source = clean(params.utm_source, 100).toLowerCase();
  let medium = clean(params.utm_medium, 100).toLowerCase();
  const campaign = clean(params.utm_campaign, 100);
  const rdtCid = clean(params.rdt_cid, 200);

  if (source) {
    medium = medium || channelFor(source)?.medium || "(not set)";
  } else if (rdtCid) {
    source = "reddit";
    medium = medium || "paid";
  } else if (params.gclid || params.gbraid || params.wbraid) {
    source = "google";
    medium = medium || "cpc";
  } else if (params.msclkid) {
    source = "bing";
    medium = medium || "cpc";
  } else if (referrerDomain) {
    const channel = channelFor(referrerDomain);
    source = channel?.name || referrerDomain;
    medium = medium || channel?.medium || "referral";
  } else if (params.fbclid) {
    // Facebook adds fbclid to ordinary link clicks too, so it says where the reader came from, not that it was an ad.
    source = "facebook";
    medium = medium || "social";
  } else {
    source = "(direct)";
    medium = medium || "(none)";
  }

  const record: FirstTouch = {
    source,
    medium,
    campaign,
    referrer_domain: clean(referrerDomain, 253),
    landing_path: clean((input.pathname || "/").split(/[?#]/)[0] || "/", 200),
    ts: (input.now ?? new Date()).toISOString(),
  };
  if (rdtCid) record.rdt_cid = rdtCid;
  return record;
}

let memory: FirstTouch | null = null;

function isFirstTouch(value: unknown): value is FirstTouch {
  const v = value as FirstTouch | null;
  return !!v && typeof v === "object" && typeof v.source === "string" && typeof v.medium === "string" && !!v.source;
}

/** The stored first touch, or this page load's when storage is unavailable, or null before capture. */
export function getFirstTouch(): FirstTouch | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isFirstTouch(parsed)) return parsed;
    }
  } catch {
    /* storage blocked or the value is corrupt */
  }
  return memory;
}

/**
 * Record the first touch if this browser has none yet. Call once at boot, before anything rewrites the URL (main.tsx
 * does, right after captureAttribution()). Later visits never overwrite it.
 */
export function captureFirstTouch(now: Date = new Date()): FirstTouch | null {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  const existing = getFirstTouch();
  if (existing) return existing;
  try {
    const record = classifyFirstTouch({
      search: window.location.search,
      referrer: document.referrer,
      pathname: window.location.pathname,
      hostname: window.location.hostname,
      stored: getAttribution(),
      now,
    });
    memory = record;
    try {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(record));
    } catch {
      /* private mode or a full quota: the record lives for this page load only */
    }
    return record;
  } catch {
    return null;
  }
}

/** Test hook: forget the in-memory copy. */
export function resetFirstTouchMemory(): void {
  memory = null;
}

/**
 * The first touch as signup tags, for email_signups.tags and the Beehiiv sync, which accept tags but have no column
 * for it: first_source:google, first_medium:organic, first_campaign:..., first_referrer:..., first_landing:/...,
 * first_seen:2026-10-24. The Reddit click id stays out: it identifies one ad click and has no use in the newsletter
 * tool. A tag is at most 120 characters.
 */
export function firstTouchTags(touch: FirstTouch | null): string[] {
  if (!touch) return [];
  const tags = [`first_source:${touch.source}`, `first_medium:${touch.medium}`];
  if (touch.campaign) tags.push(`first_campaign:${touch.campaign}`);
  if (touch.referrer_domain) tags.push(`first_referrer:${touch.referrer_domain}`);
  if (touch.landing_path) tags.push(`first_landing:${touch.landing_path}`);
  if (touch.ts) tags.push(`first_seen:${touch.ts.slice(0, 10)}`);
  return tags.map((tag) => tag.slice(0, 120));
}
